/**
 * Shape Node Component
 * Renders geometric shapes like draw.io - the SVG IS the node, no container!
 */

import { memo, useState, useEffect, useRef } from 'react'
import { Handle, Position, NodeProps, NodeResizer, useUpdateNodeInternals, useReactFlow } from '@xyflow/react'
import { sanitizeSvg } from '../../utils/sanitizeSvg'
import RotateHandle from './RotateHandle'
import { NodeConnectionHandles } from './NodeConnectionHandles'

interface ShapeNodeData {
  label: string
  shapeId: string
  shapeCategory: string
  icon: string  // Path to SVG shape
  bgColor?: string
  borderColor?: string
  borderWidth?: number
  textColor?: string
  fontSize?: number
  fillOpacity?: number
  resizedWidth?: number  // Track current dimensions for dynamic handles
  resizedHeight?: number
  minWidth?: number  // Min/max resize limits
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

interface HandleConfig {
  id: string
  x: string  // CSS position (percentage)
  y: string  // CSS position (percentage)
  position: Position
}

/**
 * Calculate handle positions based on actual shape geometry and dimensions
 * Handles stay on the shape edge, not the bounding box
 */
function getHandlesForShape(shapeId: string, width: number, height: number): HandleConfig[] {
  const id = shapeId.toLowerCase()
  const cx = width / 2
  const cy = height / 2
  const rx = width / 2
  const ry = height / 2

  // Circle/Ellipse: 8 handles on actual ellipse perimeter
  if (id.includes('circle') || id.includes('ellipse')) {
    const angles = [0, 45, 90, 135, 180, 225, 270, 315]
    return angles.map((angle, i) => {
      const rad = (angle * Math.PI) / 180
      const x = cx + rx * Math.cos(rad)
      const y = cy + ry * Math.sin(rad)
      const positions = [Position.Right, Position.Right, Position.Bottom, Position.Left, Position.Left, Position.Left, Position.Top, Position.Right]
      return {
        id: `angle-${angle}`,
        x: `${(x / width) * 100}%`,
        y: `${(y / height) * 100}%`,
        position: positions[i],
      }
    })
  }

  // Diamond: 4 handles at actual diamond vertices
  if (id.includes('diamond') || id.includes('rhombus')) {
    return [
      { id: 't', x: '50%', y: '0%', position: Position.Top },
      { id: 'r', x: '100%', y: '50%', position: Position.Right },
      { id: 'b', x: '50%', y: '100%', position: Position.Bottom },
      { id: 'l', x: '0%', y: '50%', position: Position.Left },
    ]
  }

  // Triangle: 6 handles (3 vertices + 3 edge midpoints)
  if (id.includes('triangle')) {
    // Assuming upward-pointing triangle
    return [
      { id: 't', x: '50%', y: '10%', position: Position.Top },       // Top vertex
      { id: 'tr', x: '75%', y: '55%', position: Position.Right },    // Right edge midpoint
      { id: 'br', x: '90%', y: '90%', position: Position.Right },    // Bottom-right vertex
      { id: 'b', x: '50%', y: '90%', position: Position.Bottom },    // Bottom edge midpoint
      { id: 'bl', x: '10%', y: '90%', position: Position.Left },     // Bottom-left vertex
      { id: 'tl', x: '25%', y: '55%', position: Position.Left },     // Left edge midpoint
    ]
  }

  // Pentagon/Hexagon/Octagon: handles around perimeter
  if (id.includes('pentagon') || id.includes('hexagon') || id.includes('octagon')) {
    const numSides = id.includes('pentagon') ? 5 : id.includes('hexagon') ? 6 : 8
    const angles = Array.from({ length: numSides }, (_, i) => (i * 360) / numSides - 90)
    return angles.map((angle, i) => {
      const rad = (angle * Math.PI) / 180
      const x = cx + rx * Math.cos(rad) * 0.9 // 90% to stay inside shape
      const y = cy + ry * Math.sin(rad) * 0.9
      const positions = angle < -45 || angle > 135 ? Position.Top : angle < 45 ? Position.Right : angle < 135 ? Position.Bottom : Position.Left
      return {
        id: `side-${i}`,
        x: `${(x / width) * 100}%`,
        y: `${(y / height) * 100}%`,
        position: positions,
      }
    })
  }

  // Rectangle/Square/Rounded: 8 handles at edges
  if (id.includes('rectangle') || id.includes('square') || id.includes('rounded')) {
    return [
      { id: 't', x: '50%', y: '0%', position: Position.Top },
      { id: 'tr', x: '100%', y: '0%', position: Position.Top },
      { id: 'r', x: '100%', y: '50%', position: Position.Right },
      { id: 'br', x: '100%', y: '100%', position: Position.Bottom },
      { id: 'b', x: '50%', y: '100%', position: Position.Bottom },
      { id: 'bl', x: '0%', y: '100%', position: Position.Bottom },
      { id: 'l', x: '0%', y: '50%', position: Position.Left },
      { id: 'tl', x: '0%', y: '0%', position: Position.Top },
    ]
  }

  // Default: 8 handles around perimeter
  return [
    { id: 't', x: '50%', y: '0%', position: Position.Top },
    { id: 'tr', x: '85%', y: '15%', position: Position.Right },
    { id: 'r', x: '100%', y: '50%', position: Position.Right },
    { id: 'br', x: '85%', y: '85%', position: Position.Right },
    { id: 'b', x: '50%', y: '100%', position: Position.Bottom },
    { id: 'bl', x: '15%', y: '85%', position: Position.Left },
    { id: 'l', x: '0%', y: '50%', position: Position.Left },
    { id: 'tl', x: '15%', y: '15%', position: Position.Left },
  ]
}

function ShapeNode({ data, selected, id }: NodeProps) {
  const nodeData = data as unknown as ShapeNodeData
  const [svgContent, setSvgContent] = useState<string>('')
  const updateNodeInternals = useUpdateNodeInternals()
  const { setNodes, getNode } = useReactFlow()

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(nodeData.label || '')
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Update local edit value when node label changes externally
  useEffect(() => {
    setEditValue(nodeData.label || '')
  }, [nodeData.label])

  // Handle double-click to start editing
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  // Save changes
  const saveEdit = () => {
    const trimmed = editValue.trim()
    if (trimmed !== nodeData.label) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, label: trimmed } }
            : node
        )
      )
    }
    setIsEditing(false)
  }

  // Cancel editing
  const cancelEdit = () => {
    setEditValue(nodeData.label || '')
    setIsEditing(false)
  }

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // Handle click outside (blur)
  const handleBlur = () => {
    saveEdit()
  }

  // Get current node dimensions from React Flow
  // Check the actual node dimensions in real-time
  const currentNode = getNode(id)
  // Honor an explicit dimension (set by the properties panel or NodeResizer, which
  // both write node.width/height live) over the auto-measured size, so the Width/
  // Height inputs actually resize the shape. `measured` remains the fallback for
  // nodes that have no explicit dimension yet. Shapes are fixed-size (content-driven
  // auto-sizing lives in the structured node components, not here), so this is safe.
  const nodeWidth = currentNode?.width || nodeData.resizedWidth || currentNode?.measured?.width || 120
  const nodeHeight = currentNode?.height || nodeData.resizedHeight || currentNode?.measured?.height || 120

  // Readable defaults: a white-filled shape with a dark outline and dark text.
  // (Previously these all defaulted to #000000, so MCP/shape nodes with no
  // explicit style rendered as solid-black blobs with invisible black text.)
  const bgColor = nodeData.bgColor !== undefined ? nodeData.bgColor : '#ffffff'
  const borderColor = nodeData.borderColor !== undefined ? nodeData.borderColor : '#334155'
  const borderWidth = nodeData.borderWidth !== undefined ? nodeData.borderWidth : 2
  const textColor = (nodeData as any).textColor || '#1e293b'
  const fontSize = (nodeData as any).fontSize || 14
  const fontWeight = (nodeData as any).fontWeight || '500'
  const fontFamily = (nodeData as any).fontFamily || 'Inter, sans-serif'
  const textAlign = (nodeData as any).textAlign || 'center'
  const verticalAlign = (nodeData as any).verticalAlign || 'middle'
  const lineHeight = (nodeData as any).lineHeight || 1.5
  const textDecoration = (nodeData as any).textDecoration || 'none'
  const wordWrap = (nodeData as any).wordWrap !== false
  const textOverflow = (nodeData as any).textOverflow || 'clip'
  const letterSpacing = (nodeData as any).letterSpacing || 0
  const textOpacity = (nodeData as any).textOpacity !== undefined ? (nodeData as any).textOpacity : 1
  const fillOpacity = nodeData.fillOpacity !== undefined ? nodeData.fillOpacity : 1
  // Rotation (degrees) applied to the shape's visual content (SVG + label).
  // NOTE: connection handles attach to the un-rotated bounding box.
  const rotation = (nodeData as any).rotation || 0
  const minWidth = nodeData.minWidth || 25
  const minHeight = nodeData.minHeight || 25
  const maxWidth = nodeData.maxWidth || 1000
  const maxHeight = nodeData.maxHeight || 1000

  // Get shape-specific handle positions (recalculates when dimensions change)
  const handles = getHandlesForShape(nodeData.shapeId || '', nodeWidth, nodeHeight)

  // Update React Flow internals when dimensions change
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, nodeWidth, nodeHeight, updateNodeInternals])

  // Fetch and inline SVG for proper color control
  useEffect(() => {
    if (!nodeData.icon) return

    fetch(nodeData.icon)
      .then(res => res.text())
      .then(svgText => {
        // Apply dynamic styling to SVG elements AND make it scale
        const styledSvg = svgText
          .replace(/fill="currentColor"/g, `fill="${bgColor}" fill-opacity="${fillOpacity}"`)
          .replace(/stroke="#000"/g, `stroke="${borderColor}"`)
          .replace(/stroke-width="\d+"/g, `stroke-width="${borderWidth}"`)
          // Remove fixed width/height to allow scaling, preserve viewBox
          .replace(/<svg([^>]*)width="[^"]*"/, '<svg$1')
          .replace(/<svg([^>]*)height="[^"]*"/, '<svg$1')
          // Remove any existing preserveAspectRatio to override it
          .replace(/preserveAspectRatio="[^"]*"/, '')
          // Add width/height 100% AND preserveAspectRatio="none" to allow stretching
          .replace(/<svg/, '<svg width="100%" height="100%" preserveAspectRatio="none"')
        // Sanitize before inlining via dangerouslySetInnerHTML to prevent XSS
        // from attacker-controlled SVG content (scripts, on* handlers, etc.).
        setSvgContent(sanitizeSvg(styledSvg))
      })
      .catch(err => console.error('Failed to load SVG:', err))
  }, [nodeData.icon, bgColor, borderColor, borderWidth, fillOpacity])

  return (
    <div
      className="shape-node"
      role="group"
      aria-label={`${nodeData.label || nodeData.shapeId || 'shape'} shape`}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // NO BACKGROUND, NO BORDER - Let the SVG show!
        background: 'transparent',
        border: 'none',
        cursor: 'move',
      }}
    >
      {/* Resize handles - square shaped, blue, only on corners */}
      <NodeResizer
        isVisible={selected}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        onResizeEnd={(_event, params) => {
          // Update node data with final dimensions after resize completes
          setNodes((nds) =>
            nds.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      resizedWidth: params.width,
                      resizedHeight: params.height,
                    },
                  }
                : node
            )
          )
        }}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 0,
          backgroundColor: '#3b82f6',
          border: '2px solid white',
          zIndex: 100,
        }}
        lineStyle={{
          borderWidth: 1,
          borderColor: '#3b82f6',
        }}
      />

      {/* Drag-rotate handle (single selection) */}
      {selected && <RotateHandle nodeId={id} />}

      {/* SVG Shape - inlined for full color control */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          // Selection glow
          filter: selected
            ? 'drop-shadow(0 0 8px #f59e0b) drop-shadow(0 0 12px #f59e0b)'
            : 'none',
          transition: 'filter 0.2s ease',
          zIndex: 1,
          pointerEvents: 'none', // Allow clicking through to resize handles
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          transformOrigin: 'center',
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />

      {/* Label - Editable on double-click */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: fontSize,
            fontWeight: '500',
            color: textColor,
            textAlign: 'center',
            border: '2px solid #3b82f6',
            borderRadius: '4px',
            padding: '4px 8px',
            background: 'white',
            outline: 'none',
            zIndex: 20,
            minWidth: '100px',
          }}
        />
      ) : (
        (nodeData.label && nodeData.label !== nodeData.shapeId) && (
          <div
            onDoubleClick={handleDoubleClick}
            style={{
              position: 'absolute',
              top: verticalAlign === 'top' ? '10%' : verticalAlign === 'bottom' ? '90%' : '50%',
              left: '50%',
              transform: `translate(-50%, ${verticalAlign === 'top' ? '0%' : verticalAlign === 'bottom' ? '-100%' : '-50%'})${rotation ? ` rotate(${rotation}deg)` : ''}`,
              fontSize: fontSize,
              fontWeight: fontWeight,
              fontFamily: fontFamily,
              color: textColor,
              textAlign: textAlign as any,
              lineHeight: lineHeight,
              textDecoration: textDecoration,
              letterSpacing: `${letterSpacing}px`,
              opacity: textOpacity,
              pointerEvents: 'auto',
              cursor: 'text',
              whiteSpace: wordWrap ? 'normal' : 'nowrap',
              maxWidth: '90%',
              wordBreak: wordWrap ? 'break-word' : 'normal',
              overflow: textOverflow === 'visible' ? 'visible' : textOverflow === 'clip' ? 'clip' : 'hidden',
              textOverflow: textOverflow === 'ellipsis' ? 'ellipsis' : 'clip',
              textShadow: selected ? '0 0 4px rgba(245, 158, 11, 0.5)' : 'none',
              zIndex: 10,
            }}
          >
            {nodeData.label}
          </div>
        )
      )}

      {/* Standard 12-handle set (hidden) so edges routed by autoAssignHandles
          (ids like right-top-source) resolve and render — without this the
          shape's custom geometry-handle ids don't match and edges are dropped. */}
      <NodeConnectionHandles />

      {/* Dynamic connection handles based on shape geometry (visible on hover) */}
      {handles.map((handle) => (
        <Handle
          key={handle.id}
          id={handle.id}
          type="source"
          position={handle.position}
          style={{
            left: handle.x,
            top: handle.y,
            transform: 'translate(-50%, -50%)',
            background: '#555',
            width: '10px',
            height: '10px',
            border: '2px solid white',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            position: 'absolute',
          }}
          className="shape-handle"
        />
      ))}
      {/* Target handles (same positions as source for bidirectional connections) */}
      {handles.map((handle) => (
        <Handle
          key={`target-${handle.id}`}
          id={`target-${handle.id}`}
          type="target"
          position={handle.position}
          style={{
            left: handle.x,
            top: handle.y,
            transform: 'translate(-50%, -50%)',
            background: '#555',
            width: '10px',
            height: '10px',
            border: '2px solid white',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            position: 'absolute',
          }}
          className="shape-handle"
        />
      ))}
    </div>
  )
}

export default memo(ShapeNode)
